import {
  Grid,
  Table,
  DiffTable
} from '@oliasoft-open-source/react-ui-library';
import { isNumeric, round } from '@oliasoft-open-source/units';
import translations from 'client/internationalization/translation-map.json';
import { getData } from 'common/table-viewer/get-table-data';
import { IObject } from 'common/interfaces/hierarchy-entities';
import { TCommonTableCell } from 'common/interfaces/objects-interfaces';
import { TFunction } from 'i18next';
import {
  ChangeEvent,
  useEffect, useState,
} from 'react';
import { getAllAssociatedSurveyRunsOptions } from 'client/store/entities/object/selectors';
import { useSelector } from 'react-redux';
import { ReactUINodes } from 'common/enums/common';
import { TSelectOption } from 'client/components/edm/archive-selector';
import { getKeysFromEntityType } from 'common/table-viewer/utils';
import { EntityType } from 'common/enums/entity-enums';
import { createHeaders } from '../utils/cell-creators';
import { formatTableTooltip } from './format-table-tooltip';
import { renderMathJax } from './render-math';
import { SlOptionsVertical } from "react-icons/sl";
import { surveyRunLinkedEntityTypes } from './table-viewer';
import { ModalType } from 'common/enums/information-enums';

export type TChangeOptionsPayload = {
  dataIndex: number;
  value: string;
  name: string;
  units: {
    [key: string]: string;
  }
};
interface IDataTableProps {
  dataObject: IObject;
  t: TFunction;
  hasVectors: boolean;
  visibleColumns: string[];
  handleUpdateOptions: (props: TChangeOptionsPayload) => void;
  handleLinkedObjectClick: (value: string, modalType: ModalType) => void;
  unitTemplate: { [key: string]: string };
  dataToCompare?: { [k: string]: any };
}

type TableRowCell = {
  value: string;
  type: string;
  name: string;
} | number | string;

type TableRowCellObject = Extract<TableRowCell, object>;

const pagesOptions = [20, 50, 100, 200, 500, 0];

/**
 * Extracts the first valid sortable value from a row as a number to use as a sort key.
 * Prioritizes numbers, object `value` properties, and valid date strings
 * 
 * @param row - An array of table cells representing a single row.
 * @returns The extracted numeric sort key, or 0 if no valid number is found.
 */
const getRowSortValue = (row: TableRowCell[]): number => {
  for (const cell of row) {
    if (typeof cell === 'number') {
      return cell;
    }
    if (typeof cell === 'object' && cell !== null && 'value' in cell) {
      const parsed = Number(cell.value);
      if (!isNaN(parsed)) return parsed;
    }
    if (typeof cell === 'string' && !isNaN(Date.parse(cell))) {
      return new Date(cell).getTime();
    }
  }
  return 0;
};

const sortRows = (arr: TableRowCell[][]): TableRowCell[][] => {
  return arr
    .map((row) => ({
      row,
      sortKey: getRowSortValue(row),
    }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map((item) => item.row);
};

const getOptionCell = (k: any, options: TSelectOption[], onChangeOptions: (payload: TChangeOptionsPayload) => void, units: any, index: number) => {
  const value = options?.find(i => i.value === k.value);
  return {
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      const { target: { value, name } } = event;
      onChangeOptions({
        dataIndex: index + 1, // first row units
        value,
        name,
        units,
      });
    },
    options,
    type: 'Select',
    value,
    name: k.name,
  };
};

const getCell = (k: string | number | { type: string }, config: {
  options?: TSelectOption[], onChangeOptions: (payload: TChangeOptionsPayload) => void,
  units: {
    [k: string]: string
  },
  readOnly?: boolean,
}, index: number) => {
  if (typeof k === 'object' && 'type' in k && k?.type === ReactUINodes.Select) {
    if (config.readOnly) {
      const { value } = k as TableRowCellObject;
      return { value: config.options?.find(i => i.value === value)?.label ?? value };
    }
    return getOptionCell(k, config.options!, config.onChangeOptions, config.units, index);
  }
  return { value: isNumeric(k) ? round((k as number), 3) : k };
};

const prepareRows = ({
  units,
  rows,
  tooltips,
  t,
  options,
  onChangeOptions,
  handleLinkedObjectClick,
  isSurveyRunLinkedEntity,
  readOnly,
}: {
    rows: TableRowCell[][];
    tooltips?: (string | string[])[];
    t?: TFunction;
    options?: TSelectOption[];
    units: {
      [k: string]: string
    };
    handleLinkedObjectClick: (value: string, modalType: ModalType) => void;
    isSurveyRunLinkedEntity?: boolean;
    onChangeOptions: (props: TChangeOptionsPayload) => void;
    readOnly?: boolean;
}) => {
  if (!rows) {
    return [];
  }
  const copy = rows.slice();
  const sortedRows = [...sortRows(copy)];

  return sortedRows?.map((row: TableRowCell[], i) => {
    const tooltip = tooltips?.[i] || '';
    const actions = [
      {
        icon: 'help',
        tooltip: <Grid>{formatTableTooltip(tooltip, t)}</Grid>,
      },
      isSurveyRunLinkedEntity && {
        icon: <SlOptionsVertical />,
        subActions: [
          {
            icon: 'link',
            label: t && t(translations.objects_surveyItemNavigation),
            tooltip: t && t(translations.objects_surveyItemNavigation),
            onClick: () => handleLinkedObjectClick((row?.[0] as TableRowCellObject)?.value, ModalType.MultipleLinkedItems)
          },
          {
            icon: 'eye',
            label: t && t(translations.objects_viewAssociatedData),
            tooltip: t && t(translations.objects_viewAssociatedData),
            onClick: () => handleLinkedObjectClick((row?.[0] as TableRowCellObject)?.value, ModalType.AssociatedData),
          },
        ]
      }
    ];
  
    return {
      id: i,
      cells: row?.map(k => getCell(k, { options, onChangeOptions, units, readOnly }, i)),
      actions: actions,
    };
  });
};

export const DataTable = ({
  dataObject,
  t,
  hasVectors,
  visibleColumns,
  handleUpdateOptions,
  handleLinkedObjectClick,
  unitTemplate,
  dataToCompare,
}
: IDataTableProps) => {
  const tableData = getData(dataObject, unitTemplate, hasVectors, visibleColumns, renderMathJax);
  const {
    headers, rows, units: unitRow, tooltips: info, quantities,
  } = tableData;
  const [data, setData] = useState(rows);
  const [units, setUnits] = useState(unitRow);

  const associatedSurveyRunsOptions = useSelector(getAllAssociatedSurveyRunsOptions);
  const isSurveyRunLinkedEntity = surveyRunLinkedEntityTypes.includes(dataObject.type as EntityType);

  useEffect(() => {
    setData(getData(dataObject, unitTemplate, hasVectors, visibleColumns, renderMathJax).rows);
    setUnits(unitRow);
  }, [dataObject, hasVectors, visibleColumns]);

  const [rowsPerPage, setRowPerPage] = useState(50);
  const [selectedPage, setSelectedPage] = useState(1);

  const translatedPage = t(translations.page);
  const tableOptions = pagesOptions.map(pageNumber => ({
    label: pageNumber === 0 ? t(translations.viewer_showAll) : `${pageNumber} / ${translatedPage}`, value: pageNumber === 0 ? rows.length : pageNumber,
  }));

  const keys = getKeysFromEntityType(dataObject.type as EntityType, visibleColumns);

  const unitsObjectRow = keys.reduce((acc, key, index) => {
    return { ...acc, [key]: units[index] };
  }, {});

  const preparedRows = prepareRows({
    units: unitsObjectRow,
    rows: data as TableRowCell[][],
    tooltips: info,
    t,
    options: associatedSurveyRunsOptions,
    onChangeOptions: handleUpdateOptions,
    handleLinkedObjectClick,
    isSurveyRunLinkedEntity,
    readOnly: !!dataToCompare,
  }) as any;

  const firstVisibleRow = (selectedPage - 1) * rowsPerPage;
  const lastVisibleRow = firstVisibleRow + rowsPerPage;
  // TODO update this function to skip first row after implementing units
  const viewRows = dataToCompare ? preparedRows : preparedRows.slice(firstVisibleRow, lastVisibleRow);
  const headerRows = createHeaders(headers, quantities || [], units, (units: (string)[]) => setUnits(units), data, (data: TCommonTableCell[][]) => setData(data), 'table', !!dataToCompare);

  const table = {
    headers: headerRows,
    stickyHeaders: 'true',
    rows: viewRows,
    bordered: false,
    infiniteScroll: true,
    estimateRowHeight: () => 40,
    maxHeight: '80vh',
    ...(dataToCompare ? {} : {
      footer: {
        pagination: {
          rowCount: rows.length,
          selectedPage,
          rowsPerPage: {
            onChange: ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
              setSelectedPage(+value > rowsPerPage ? 1 : selectedPage);
              setRowPerPage(+value || 10);
            },
            options: tableOptions,
            value: rowsPerPage,
          },
          onSelectPage: (value: number) => setSelectedPage(+value || 1),
          small: true,
        },
      },
    }),
    columnAlignment: [...viewRows].map(() => 'right'),
  };

  if (dataToCompare) {
    const rowsData = getData({ ...dataObject, data: dataToCompare }, unitTemplate, hasVectors, visibleColumns, renderMathJax)?.rows;
    const rowsToCompare = prepareRows({
      units: unitsObjectRow,
      rows: rowsData || [],
      tooltips: info,
      t,
      options: associatedSurveyRunsOptions,
      onChangeOptions: handleUpdateOptions,
      handleLinkedObjectClick,
      isSurveyRunLinkedEntity,
      readOnly: true,
    }) as any;
    return <DiffTable table={table} compareRows={rowsToCompare} />
  }

  return <Table table={table} />;
};
